import CONSTANTS from 'src/utils/constants';
import { withWizardStep } from 'src/components/calculation-builder/wizardStepDeepLink';

/**
 * CLI → Calc Builder link for the LEF report (Epic 3, Story 3.5).
 *
 * LEF payloads carry CLI numbers but not the CLI recordId the Calc Builder route needs
 * (/calculations/builder/:encodedCliRecordId, with the recordId base64-encoded — the same encoding
 * MAR's "Manage PLI Configuration" button uses). So the recordId is looked up when the link is
 * clicked. Calc Builder does not read a period from its URL, so the link carries none; it does read
 * an optional `?step=N`, which the GL source chip uses to land on Step 2 (Data Sources).
 */

export const CALC_BUILDER_NO_ACCESS_MESSAGE = 'Your role is not authorized to view Calculation Builder.';
export const CALC_BUILDER_LOOKUP_FAILED_MESSAGE = 'Could not open Calc Builder for this CLI.';
export const CALC_BUILDER_POPUP_BLOCKED_MESSAGE = 'Your browser blocked the new tab. Allow pop-ups for this site and try again.';

export function buildCalcBuilderUrl(recordId: string, step?: number): string {
    return withWizardStep(`${CONSTANTS.PAGE_NAV.CALCULATION_BUILDER.URL}/${btoa(recordId)}`, step);
}

/**
 * Opens the CLI's Calc Builder page in a new tab. The tab is opened synchronously, inside the click,
 * and pointed at the page once the recordId is known: opening it only after the await would be
 * treated as an unsolicited popup and blocked. On failure the placeholder tab is closed and the
 * error is rethrown for the caller to show. `step` deep-links to a wizard step (see
 * wizardStepDeepLink.ts); without it Calc Builder picks its usual landing step.
 */
export async function openCliInCalcBuilder(
    cli: string,
    lookupRecordId: (cli: string) => Promise<string | undefined | null>,
    browserWindow: Pick<Window, 'open'> = window,
    step?: number
): Promise<void> {
    const tab = browserWindow.open('', '_blank');
    if (tab) {
        // The new tab is same-origin; drop the back-reference as noopener would.
        tab.opener = null;
    }
    try {
        const recordId = await lookupRecordId(cli);
        if (!recordId) {
            throw new Error(CALC_BUILDER_LOOKUP_FAILED_MESSAGE);
        }
        const url = buildCalcBuilderUrl(recordId, step);
        if (tab) {
            tab.location.href = url;
        } else {
            // The browser refused the placeholder tab; try once more now that we have the URL. Without the
            // 'noopener' feature, which would make open() return null even on success, so a refusal is
            // detectable; the back-reference is dropped by hand instead.
            const retryTab = browserWindow.open(url, '_blank');
            if (!retryTab) {
                throw new Error(CALC_BUILDER_POPUP_BLOCKED_MESSAGE);
            }
            retryTab.opener = null;
        }
    } catch (error) {
        tab?.close();
        throw error;
    }
}
